import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/utils";
import { Key, Plus, ShieldOff, Trash2, Copy, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function ApiKeys() {
  const [page, setPage] = useState(1);
  const [companyFilter, setCompanyFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newCompanyId, setNewCompanyId] = useState("");
  const [createdRawKey, setCreatedRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError, refetch } = trpc.admin.apiKeys.list.useQuery({
    page,
    limit: 20,
    companyId: companyFilter || undefined,
  });

  const createMutation = trpc.admin.apiKeys.create.useMutation({
    onSuccess: (result) => {
      setCreatedRawKey(result.key);
      setNewKeyName("");
      setNewCompanyId("");
      setShowCreate(false);
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const revokeMutation = trpc.admin.apiKeys.revoke.useMutation({
    onSuccess: () => {
      toast.success("Clé révoquée");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.admin.apiKeys.delete.useMutation({
    onSuccess: () => {
      toast.success("Clé supprimée");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  function handleCopy() {
    if (!createdRawKey) return;
    navigator.clipboard.writeText(createdRawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Clés API</h1>
            <p className="text-muted-foreground">Gestion des clés API par entreprise</p>
          </div>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle clé
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <CardTitle>Toutes les clés</CardTitle>
              <Input
                placeholder="Filtrer par company ID..."
                className="max-w-xs"
                value={companyFilter}
                onChange={(e) => {
                  setCompanyFilter(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <AlertCircle className="h-12 w-12 text-destructive/40" />
                <p className="text-muted-foreground font-medium">Erreur de chargement des clés API</p>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réessayer
                </Button>
              </div>
            ) : data && data.items.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Clé (masquée)</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Dernière utilisation</TableHead>
                      <TableHead>Expire le</TableHead>
                      <TableHead>Créée le</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((k: any) => (
                      <TableRow key={k.id}>
                        <TableCell className="font-medium">{k.name}</TableCell>
                        <TableCell>{k.Company?.name || k.companyId}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">{k.key}</code>
                        </TableCell>
                        <TableCell>
                          <Badge variant={k.isActive ? "default" : "secondary"}>
                            {k.isActive ? "Active" : "Révoquée"}
                          </Badge>
                        </TableCell>
                        <TableCell>{k.lastUsedAt ? formatDate(k.lastUsedAt) : "—"}</TableCell>
                        <TableCell>{k.expiresAt ? formatDate(k.expiresAt) : "—"}</TableCell>
                        <TableCell>{formatDate(k.createdAt)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {k.isActive && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => revokeMutation.mutate({ id: k.id })}
                                disabled={revokeMutation.isPending}
                              >
                                <ShieldOff className="h-3 w-3 mr-1" />
                                Révoquer
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteMutation.mutate({ id: k.id })}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {data.total > data.limit && (
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-muted-foreground">
                      Page {data.page} / {Math.ceil(data.total / data.limit)}
                    </span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        Précédent
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={page >= Math.ceil(data.total / data.limit)}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Key className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground">Aucune clé API</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une clé API</DialogTitle>
              <DialogDescription>
                Générer une nouvelle clé API pour une entreprise.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label htmlFor="apikey-company-id" className="text-sm font-medium">
                  Company ID
                </label>
                <Input
                  id="apikey-company-id"
                  placeholder="uuid de l'entreprise..."
                  value={newCompanyId}
                  onChange={(e) => setNewCompanyId(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="apikey-name" className="text-sm font-medium">
                  Nom de la clé
                </label>
                <Input
                  id="apikey-name"
                  placeholder="ex: Integration Salesforce"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => createMutation.mutate({ companyId: newCompanyId, name: newKeyName })}
                disabled={!newCompanyId || !newKeyName || createMutation.isPending}
              >
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Show raw key after creation */}
        <Dialog open={!!createdRawKey} onOpenChange={() => setCreatedRawKey(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clé créée — copiez-la maintenant</DialogTitle>
              <DialogDescription>
                Cette clé ne sera plus visible après fermeture de cette fenêtre.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-muted rounded-md p-3 font-mono text-sm break-all">{createdRawKey}</div>
            <DialogFooter>
              <Button onClick={handleCopy}>
                {copied ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? "Copié !" : "Copier"}
              </Button>
              <Button variant="outline" onClick={() => setCreatedRawKey(null)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
